import { z } from "zod";
import {
  buildBlock,
  getLibraryBlock,
  type PlacedElements,
  placeSavedElements,
  type SavedElements,
  type SavedLook,
} from "../blocks";
import { bindColor, bindTextStyle, brandScale, unbindElement } from "../brand/apply";
import { type BrandTokens, brandColor, brandTextStyle } from "../brand/tokens";
import {
  deviceBoxSize,
  deviceCanRotate,
  deviceContentAspect,
  deviceForScreenshot,
  deviceModel,
  devicePerspectives,
} from "../devices";
import { diff, type LiveOp } from "../live/ops";
import { brandChartPalette, CHART_KINDS, parseChartTable } from "../scene/chart";
import { contrastColor, paintPreviewColor } from "../scene/color";
import { boundingRect, buildParentMap, elementBox, type Rect, worldBox } from "../scene/geometry";
import {
  addPage,
  clone,
  createChartElement,
  createDeviceElement,
  createIconElement,
  createImageElement,
  createQrElement,
  createShapeElement,
  createSvgElement,
  createTextElement,
  duplicateElements,
  duplicatePage,
  fitSize,
  groupElements,
  imageFromPlaceholder,
  insertElements,
  isImagePlaceholder,
  movePage,
  normalizeGroup,
  type ReorderMode,
  removeElements,
  removePage,
  reorderElements,
  resizePage,
  scaleContent,
  setWorldBox,
  ungroupElement,
} from "../scene/ops";
import { pageSize } from "../scene/pages";
import { QR_LOGO_ECC, qrMatrix } from "../scene/qr";
import { setPlainText } from "../scene/richtext";
import {
  ANIMATION_PRESETS,
  type ChartElement,
  elementAnimationSchema,
  gradientSchema,
  ICON_WEIGHTS,
  type IconPath,
  type IconWeight,
  LOOP_ANIMATIONS,
  type SceneDocument,
  type SceneElement,
  SHAPE_KINDS,
  shadowSchema,
  TEXT_MAX_LENGTH,
  type TextElement,
  textEffectSchema,
  textMarkSchema,
} from "../scene/schema";
import { createTableElement } from "../scene/table";
import { tidyLayout } from "../scene/tidy";
import { BRAND_STYLES, getBrandStyle, type TemplateLogo, type TextMeasure } from "../templates";
import { PlacementError, type PlacementInput, placementSchema, resolvePlacement } from "./place";
import { type RefTable, refOf, resolveRef, syncRefs } from "./refs";
import { restyleDocument } from "./restyle";
import { type AgentMeasure, ESTIMATED_MEASURE, fitFontSize, textContentHeight } from "./text";

/**
 * `edit_document` (docs/23-mcp-v2.md, 1.3): a list of small operations on
 * elements addressed by ref. Every operation is applied to a copy, turned into
 * live operations (`diff`) and handed back step by step, so the server can
 * play them in the open editor one after the other – or reject all of them.
 */

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

const ref = z
  .string()
  .min(1)
  .max(200)
  .describe("Element: Nummer aus describe_document (oder $alias)");
const color = z
  .string()
  .max(40)
  .describe(
    "Farbe: Hex (#rrggbb, #rrggbbaa) oder Farbschlüssel des Brand Kits (primary, accent, …)",
  );
const page1 = z.number().int().min(1).describe("Seite (1 = erste)");

const addElementSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("text"),
    text: z.string().max(TEXT_MAX_LENGTH),
    role: z.enum(["headline", "subline", "body", "caption"]).optional(),
    textStyle: z.string().max(40).optional().describe("Textstil des Brand Kits (z. B. heading-1)"),
    fontFamily: z.string().max(100).optional(),
    fontSize: z.number().positive().max(2000).optional(),
    fontWeight: z.number().int().min(100).max(900).optional(),
    color: color.optional(),
    align: z.enum(["left", "center", "right", "justify"]).optional(),
  }),
  z.object({
    kind: z.literal("shape"),
    shape: z.enum(SHAPE_KINDS),
    fill: color.optional(),
    gradient: gradientSchema.optional(),
    radius: z.number().min(0).optional(),
    stroke: z.object({ color, width: z.number().min(0).max(500) }).optional(),
  }),
  z.object({
    kind: z.literal("image"),
    assetId: z.uuid().describe("Bild oder Grafik aus list_media"),
    fit: z.enum(["cover", "contain"]).optional(),
    radius: z.number().min(0).optional(),
  }),
  z.object({
    kind: z.literal("icon"),
    name: z.string().max(80).describe("Icon-Name aus search_icons"),
    weight: z.enum(ICON_WEIGHTS).default("regular"),
    color: color.optional(),
  }),
  z.object({
    kind: z.literal("qr"),
    value: z.string().min(1).max(1000),
    color: color.optional(),
    logoAssetId: z.uuid().optional(),
  }),
  z.object({
    kind: z.literal("table"),
    rows: z
      .array(z.array(z.string().max(500)).min(1).max(12))
      .min(1)
      .max(60),
    header: z.boolean().default(true),
    zebra: z.boolean().optional(),
    fontSize: z.number().positive().max(500).optional(),
  }),
  z.object({
    kind: z.literal("chart"),
    chart: z.enum(CHART_KINDS).default("column"),
    title: z.string().max(200).default(""),
    labels: z.array(z.string().max(80)).max(60).optional(),
    series: z
      .array(
        z.object({
          name: z.string().max(80).default(""),
          values: z.array(z.number().nullable()).max(60),
        }),
      )
      .min(1)
      .max(8)
      .optional(),
    table: z
      .string()
      .max(20_000)
      .optional()
      .describe("Alternativ: Tabelle als Text (tab-/semikolongetrennt)"),
    unit: z.string().max(12).default(""),
    showValues: z.boolean().optional(),
    stacked: z.boolean().default(false),
  }),
  z.object({
    kind: z.literal("device"),
    device: z.string().max(60).optional().describe("Geräte-ID; ohne: passend zum Screenshot"),
    assetId: z.uuid().optional().describe("Screenshot aus list_media"),
    view: z.enum(["front", "right", "left", "hero", "float", "lying", "above"]).default("front"),
    url: z.string().max(200).optional(),
  }),
]);
export type AddElement = z.infer<typeof addElementSchema>;

export const editOpSchema = z.discriminatedUnion("op", [
  z.object({
    op: z.literal("set_text"),
    ref,
    text: z.string().max(TEXT_MAX_LENGTH),
    marks: z
      .array(textMarkSchema)
      .max(500)
      .optional()
      .describe("Gemischte Formatierung (from/to als Zeichenpositionen)"),
  }),
  z.object({
    op: z.literal("set_style"),
    ref,
    fontFamily: z.string().max(100).optional(),
    fontSize: z.number().positive().max(2000).optional(),
    fontWeight: z.number().int().min(100).max(900).optional(),
    italic: z.boolean().optional(),
    underline: z.boolean().optional(),
    strike: z.boolean().optional(),
    uppercase: z.boolean().optional(),
    color: color
      .optional()
      .describe("Textfarbe bzw. Farbe von Icon, QR-Code, Zeichnung (bei Formen: Füllung)"),
    align: z.enum(["left", "center", "right", "justify"]).optional(),
    verticalAlign: z.enum(["top", "middle", "bottom"]).optional(),
    lineHeight: z.number().positive().max(5).optional(),
    letterSpacing: z.number().min(-1).max(2).optional().describe("In em"),
    textStyle: z
      .string()
      .max(40)
      .optional()
      .describe("Textstil des Brand Kits übernehmen (bleibt verknüpft)"),
    opacity: z.number().min(0).max(1).optional(),
    rotation: z.number().optional().describe("Grad, im Uhrzeigersinn"),
    radius: z.number().min(0).optional().describe("Ecken-Radius (Rechteck, Bild)"),
    shadow: shadowSchema.partial().nullable().optional().describe("Schatten; null entfernt ihn"),
    effect: textEffectSchema.nullable().optional().describe("Texteffekt; null entfernt ihn"),
    name: z.string().max(200).optional(),
    hidden: z.boolean().optional(),
    locked: z.boolean().optional(),
  }),
  z.object({
    op: z.literal("set_fill"),
    ref: ref.optional(),
    page: page1.optional().describe("Seitenhintergrund statt eines Elements"),
    color: color.optional(),
    gradient: gradientSchema.optional(),
  }),
  z.object({
    op: z.literal("set_stroke"),
    ref,
    color: color.optional(),
    width: z.number().min(0).max(500).optional(),
    dash: z.enum(["solid", "dashed", "dotted"]).optional(),
    remove: z.boolean().optional(),
  }),
  z.object({ op: z.literal("move"), ref, to: placementSchema }),
  z.object({
    op: z.literal("resize"),
    ref,
    w: z.number().positive().optional(),
    h: z.number().positive().optional(),
    unit: z.enum(["px", "mm", "%"]).default("px"),
    keepRatio: z
      .boolean()
      .optional()
      .describe("Verhältnis halten (Standard bei Bildern, Icons, QR)"),
  }),
  z.object({
    op: z.literal("fit_text"),
    ref,
    minSize: z.number().positive().optional(),
    maxSize: z.number().positive().optional(),
    maxHeight: z
      .number()
      .positive()
      .optional()
      .describe("Höhe in px, in die der Text passen soll (Standard: die Boxhöhe)"),
  }),
  z.object({
    op: z.literal("replace_image"),
    ref: ref.describe("Bild oder Bildplatzhalter"),
    assetId: z.uuid(),
    fit: z.enum(["cover", "contain"]).optional(),
  }),
  z.object({
    op: z.literal("add"),
    page: page1.default(1),
    element: addElementSchema,
    at: placementSchema.optional(),
    key: z
      .string()
      .min(1)
      .max(40)
      .optional()
      .describe("Alias, mit dem spätere Operationen das neue Element als $alias ansprechen"),
  }),
  z.object({
    op: z.literal("add_block"),
    blockId: z.string().max(100).describe("ID aus search_blocks"),
    page: page1.default(1),
    at: placementSchema
      .optional()
      .describe(
        'Bereich, z. B. { zone: "top-third" } oder { below: "2" }; w skaliert den Baustein',
      ),
    style: z.string().max(40).optional(),
    atMs: z.number().min(0).optional().describe("Video: Startzeit in ms"),
    key: z.string().min(1).max(40).optional(),
  }),
  z.object({ op: z.literal("remove"), refs: z.array(ref).min(1).max(100) }),
  z.object({
    op: z.literal("duplicate"),
    refs: z.array(ref).min(1).max(50),
    offset: z.object({ x: z.number(), y: z.number() }).optional(),
    key: z.string().min(1).max(40).optional().describe("Alias für das erste Duplikat"),
  }),
  z.object({
    op: z.literal("reorder"),
    ref,
    to: z.union([
      z.enum(["front", "back", "forward", "backward"]),
      z.object({ above: ref }).strict(),
      z.object({ below: ref }).strict(),
    ]),
  }),
  z.object({
    op: z.literal("group"),
    refs: z.array(ref).min(2).max(100),
    name: z.string().max(200).optional(),
    key: z.string().min(1).max(40).optional(),
  }),
  z.object({ op: z.literal("ungroup"), ref }),
  z.object({
    op: z.literal("align"),
    refs: z.array(ref).min(1).max(100),
    edge: z.enum(["left", "center", "right", "top", "middle", "bottom"]),
    to: z.enum(["page", "selection"]).default("selection"),
  }),
  z.object({
    op: z.literal("distribute"),
    refs: z.array(ref).min(2).max(100),
    axis: z.enum(["horizontal", "vertical"]),
    gap: z
      .number()
      .optional()
      .describe("Fester Abstand in px; ohne: gleichmäßig zwischen dem ersten und letzten"),
  }),
  z.object({
    op: z.literal("tidy"),
    refs: z.array(ref).min(2).max(100),
    columns: z.number().int().min(1).max(20).optional(),
  }),
  z.object({
    op: z.literal("apply_style"),
    style: z.string().max(40).optional().describe("Design-Stil (ID aus get_context)"),
    look: z
      .string()
      .max(100)
      .optional()
      .describe(
        "Gespeicherter Look der Organisation (saved:… aus search_blocks): Bildanpassungen für die Bilder",
      ),
    page: page1.optional().describe("Nur diese Seite; ohne: ganze Datei"),
  }),
  z.object({ op: z.literal("page_add"), at: page1.optional(), background: color.optional() }),
  z.object({ op: z.literal("page_duplicate"), page: page1 }),
  z.object({ op: z.literal("page_remove"), page: page1 }),
  z.object({ op: z.literal("page_move"), page: page1, to: page1 }),
  z.object({
    op: z.literal("page_resize"),
    page: page1,
    width: z.number().int().min(16).max(20000),
    height: z.number().int().min(16).max(20000),
    scale: z.boolean().default(true).describe("Inhalt mit skalieren"),
  }),
  z.object({
    op: z.literal("set_time"),
    ref,
    start: z.number().min(0).optional().describe("ms"),
    end: z.number().min(0).optional().describe("ms"),
    clear: z.boolean().optional().describe("Zeitbereich entfernen (immer sichtbar)"),
  }),
  z.object({
    op: z.literal("set_animation"),
    ref,
    in: z
      .object({
        preset: z.enum(ANIMATION_PRESETS),
        duration: z.number().min(50).max(10_000).optional(),
      })
      .nullable()
      .optional(),
    out: z
      .object({
        preset: z.enum(ANIMATION_PRESETS),
        duration: z.number().min(50).max(10_000).optional(),
      })
      .nullable()
      .optional(),
    loop: z.enum(LOOP_ANIMATIONS).nullable().optional(),
  }),
]);
export type EditOp = z.infer<typeof editOpSchema>;
export const EDIT_MAX_OPS = 100;

// ---------------------------------------------------------------------------
// Context & result
// ---------------------------------------------------------------------------

export interface AssetFacts {
  kind: string;
  width?: number | null;
  height?: number | null;
  name?: string;
}

export interface EditContext {
  refs: RefTable;
  tokens?: BrandTokens | null | undefined;
  measure?: AgentMeasure | undefined;
  /** Facts about the media the operations name (kind, size). */
  assets?: Record<string, AssetFacts> | undefined;
  /** Icon paths by `${name}:${weight}`, loaded by the caller for `add` of icons. */
  icons?: Record<string, IconPath[]> | undefined;
  /** Known font families (built-in and the organization's); unknown ones are refused. */
  fonts?: readonly string[] | undefined;
  logos?: readonly TemplateLogo[] | undefined;
  brandName?: string | undefined;
  /** Saved blocks of the organization by `saved:<id>`. */
  savedBlocks?: Record<string, SavedElements> | undefined;
  /** Saved looks (image adjustments) by `saved:<id>`. */
  savedLooks?: Record<string, SavedLook> | undefined;
}

export interface EditStep {
  index: number;
  op: EditOp;
  /** The live operations this step amounts to (relative to the step before). */
  ops: LiveOp[];
  /** Elements the step touched (for the selection of the AI in the editor). */
  touched: string[];
  /** 0-based page the step worked on, for the editor to switch to. */
  pageIndex: number | null;
  /** Elements whose text is rewritten (locked for people typing in them). */
  rewrites: string[];
  note: string;
}

export interface EditOutcome {
  doc: SceneDocument;
  steps: EditStep[];
  /** Ids of all elements that were changed or added. */
  changed: string[];
  created: { id: string; ref: string; alias?: string }[];
  removed: string[];
}

export class EditError extends Error {
  constructor(
    message: string,
    readonly index: number,
  ) {
    super(message);
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const ROLE_STYLE_KEY = {
  headline: "heading-1",
  subline: "subline",
  body: "body",
  caption: "caption",
} as const;
/** Layer names for texts added with a role (describe_document reads the role from them). */
const ROLE_NAME = {
  headline: "Headline",
  subline: "Subline",
  body: "Text",
  caption: "Bildunterschrift",
} as const;
const ROLE_LOOK = {
  headline: { size: 0.085, weight: 800, lineHeight: 1.08 },
  subline: { size: 0.05, weight: 600, lineHeight: 1.25 },
  body: { size: 0.036, weight: 400, lineHeight: 1.3 },
  caption: { size: 0.026, weight: 500, lineHeight: 1.3 },
} as const;

interface State {
  doc: SceneDocument;
  ctx: EditContext;
  aliases: Map<string, string>;
  measure: AgentMeasure;
  touched: Set<string>;
  rewrites: Set<string>;
  pageIndex: number | null;
  created: { id: string; alias?: string }[];
  removed: string[];
  note: string;
}

class OpError extends Error {}

const fail = (message: string): never => {
  throw new OpError(message);
};

function target(state: State, key: string): string {
  if (key.startsWith("$")) {
    const id = state.aliases.get(key.slice(1));
    if (!id || !state.doc.elements[id])
      fail(`Alias „${key}“ ist unbekannt (nur Aliase aus früheren add-Operationen dieses Aufrufs)`);
    return id!;
  }
  const id = resolveRef(state.ctx.refs, state.doc, key);
  if (!id) {
    return fail(
      `Element „${key}“ gibt es nicht (mehr). Mit describe_document die aktuellen Nummern prüfen.`,
    );
  }
  return id!;
}

function element(state: State, key: string): SceneElement {
  return state.doc.elements[target(state, key)]!;
}

function pageOf(state: State, id: string): number {
  const parents = buildParentMap(state.doc);
  let current = parents.get(id);
  let guard = 0;
  while (current && guard++ < 100) {
    if (current.kind === "page") return current.pageIndex;
    current = parents.get(current.groupId);
  }
  return fail(`Element „${refOf(state.ctx.refs, id)}“ liegt auf keiner Seite`);
}

function pageIndexFor(state: State, page: number): number {
  if (!state.doc.pages[page - 1]) {
    fail(
      `Seite ${page} gibt es nicht (die Datei hat ${state.doc.pages.length} Seite${state.doc.pages.length === 1 ? "" : "n"})`,
    );
  }
  return page - 1;
}

function touch(state: State, ...ids: string[]) {
  for (const id of ids) state.touched.add(id);
}

function placeContext(state: State, pageIndex: number) {
  return {
    doc: state.doc,
    pageIndex,
    resolve: (key: string) => {
      try {
        return target(state, key);
      } catch {
        return null;
      }
    },
  };
}

/** Hex stays hex; anything else is a brand colour key (linked). */
function colorValue(state: State, value: string): { hex: string; key?: string } {
  if (HEX.test(value)) return { hex: value.toLowerCase() };
  const brand = state.ctx.tokens ? brandColor(state.ctx.tokens, value) : undefined;
  if (!brand) {
    const known = state.ctx.tokens?.colors.map((entry) => entry.key).join(", ");
    return fail(
      `Unbekannte Farbe „${value}“. Hex-Wert wie #1a2b3c angeben${known ? ` oder einen Farbschlüssel des Brand Kits (${known})` : ""}.`,
    );
  }
  return { hex: brand.color, key: value };
}

type Slot = "fill" | "stroke" | "color";

/** Sets the colour of a slot (linked to the brand colour when a key was given). */
function setColor(state: State, el: SceneElement, slot: Slot, value: string) {
  const { hex, key } = colorValue(state, value);
  if (key && state.ctx.tokens && bindColor(el, slot, key, state.ctx.tokens)) return;
  assign(el, slot, hex);
  unbindElement(el, [slot]);
}

function assign(el: SceneElement, slot: Slot, hex: string) {
  if (slot === "color") {
    if (el.type === "text" || el.type === "captions") el.style.color = hex;
    else if (el.type === "shape") el.fill = hex;
    else if (
      el.type === "qr" ||
      el.type === "icon" ||
      el.type === "path" ||
      el.type === "chart" ||
      el.type === "table"
    ) {
      el.color = hex;
    } else
      fail(
        `Dieses Element (${el.type}) hat keine Farbe zum Ändern – set_fill oder set_stroke verwenden`,
      );
  } else if (slot === "fill") {
    if (el.type === "shape") el.fill = hex;
    else if (el.type === "qr") el.background = hex;
    else if (el.type === "table") el.headerFill = hex;
    else if (el.type === "path") el.fill = hex;
    else fail(`Dieses Element (${el.type}) hat keine Füllung`);
  } else if (el.type === "shape" || el.type === "image") {
    el.stroke = { ...(el.stroke ?? { width: 4, dash: "solid" as const }), color: hex };
  } else if (el.type === "table") el.borderColor = hex;
  else fail(`Dieses Element (${el.type}) hat keine Kontur`);
}

function checkFont(state: State, family: string) {
  const known = state.ctx.fonts;
  if (known && known.length > 0 && !known.includes(family)) {
    fail(`Schrift „${family}“ gibt es nicht. Verfügbar: ${known.join(", ")}`);
  }
}

/** Recomputes the height of text boxes that follow their content. */
function refreshHeight(state: State, el: SceneElement) {
  if (el.type !== "text" || !el.autoHeight || el.curve) return;
  const height = Math.max(1, textContentHeight(el, state.measure));
  if (Math.abs(height - el.height) > 0.5) {
    el.height = height;
    const parent = buildParentMap(state.doc).get(el.id);
    if (parent?.kind === "group") normalizeGroup(state.doc, parent.groupId);
  }
}

function worldRect(state: State, id: string): Rect {
  const box = worldBox(state.doc, id);
  if (!box) return fail("Element nicht gefunden");
  return boundingRect(box);
}

/** Moves an element to a page-space top-left corner. */
function moveTo(
  state: State,
  id: string,
  x: number,
  y: number,
  size?: { width: number; height: number },
) {
  const box = worldBox(state.doc, id)!;
  setWorldBox(state.doc, id, {
    ...box,
    x,
    y,
    width: size?.width ?? box.width,
    height: size?.height ?? box.height,
  });
}

function aspectOf(el: SceneElement): number {
  return el.width / Math.max(1, el.height);
}

// ---------------------------------------------------------------------------
// Operations
// ---------------------------------------------------------------------------

function applyOp(state: State, op: EditOp) {
  const { doc } = state;
  switch (op.op) {
    case "set_text": {
      const el = element(state, op.ref);
      if (el.type !== "text") return fail(`Element „${op.ref}“ ist kein Text (${el.type})`);
      if (op.marks) {
        el.text = op.text;
        el.marks = op.marks;
        delete el.paragraphs;
      } else {
        setPlainText(el, op.text);
      }
      state.rewrites.add(el.id);
      refreshHeight(state, el);
      touch(state, el.id);
      state.pageIndex = pageOf(state, el.id);
      state.note = `ändert den Text${el.name ? ` „${el.name}“` : ""}`;
      return;
    }
    case "set_style": {
      const el = element(state, op.ref);
      applyStyle(state, el, op);
      refreshHeight(state, el);
      touch(state, el.id);
      state.pageIndex = pageOf(state, el.id);
      state.note = "passt die Gestaltung an";
      return;
    }
    case "set_fill": {
      if (op.page !== undefined) {
        const page = doc.pages[pageIndexFor(state, op.page)]!;
        if (op.gradient) {
          page.background = op.gradient;
          delete page.brand;
        } else if (op.color) {
          const { hex, key } = colorValue(state, op.color);
          page.background = hex;
          if (key) page.brand = { ...page.brand, background: key };
          else delete page.brand;
        } else fail("set_fill braucht color oder gradient");
        state.pageIndex = op.page - 1;
        state.note = "setzt den Seitenhintergrund";
        return;
      }
      if (!op.ref) return fail("set_fill braucht ref oder page");
      const el = element(state, op.ref);
      if (op.gradient) {
        if (el.type !== "shape")
          return fail("Verläufe gibt es nur bei Formen und Seitenhintergründen");
        el.fill = op.gradient;
        unbindElement(el, ["fill"]);
      } else if (op.color) {
        setColor(state, el, "fill", op.color);
      } else fail("set_fill braucht color oder gradient");
      touch(state, el.id);
      state.pageIndex = pageOf(state, el.id);
      state.note = "ändert die Füllung";
      return;
    }
    case "set_stroke": {
      const el = element(state, op.ref);
      if (el.type !== "shape" && el.type !== "image" && el.type !== "table") {
        return fail(`Dieses Element (${el.type}) hat keine Kontur`);
      }
      if (op.remove) {
        if (el.type !== "table") delete el.stroke;
        unbindElement(el, ["stroke"]);
      } else {
        if (op.color) setColor(state, el, "stroke", op.color);
        else if (el.type !== "table" && !el.stroke)
          el.stroke = { color: "#111111", width: 4, dash: "solid" };
        if (el.type !== "table" && el.stroke) {
          if (op.width !== undefined) el.stroke.width = op.width;
          if (op.dash) el.stroke.dash = op.dash;
        }
      }
      touch(state, el.id);
      state.pageIndex = pageOf(state, el.id);
      state.note = "ändert die Kontur";
      return;
    }
    case "move": {
      const id = target(state, op.ref);
      const pageIndex = pageOf(state, id);
      const el = doc.elements[id]!;
      const rect = worldRect(state, id);
      const placed = resolvePlacement(placeContext(state, pageIndex), op.to, {
        width: rect.width,
        height: rect.height,
        x: rect.x,
        y: rect.y,
      });
      if (el.type === "text" && el.autoHeight && op.to.w !== undefined) {
        el.width = placed.width;
        refreshHeight(state, el);
        moveTo(state, id, placed.x, placed.y, { width: placed.width, height: el.height });
      } else {
        moveTo(state, id, placed.x, placed.y, { width: placed.width, height: placed.height });
      }
      touch(state, id);
      state.pageIndex = pageIndex;
      state.note = "verschiebt ein Element";
      return;
    }
    case "resize": {
      const id = target(state, op.ref);
      const el = doc.elements[id]!;
      const pageIndex = pageOf(state, id);
      const size = pageSize(doc, doc.pages[pageIndex]);
      const print = doc.format.print;
      const pxPerMm = print ? doc.format.width / print.widthMm : 96 / 25.4;
      const fx = op.unit === "%" ? size.width / 100 : op.unit === "mm" ? pxPerMm : 1;
      const fy = op.unit === "%" ? size.height / 100 : op.unit === "mm" ? pxPerMm : 1;
      let width = op.w !== undefined ? op.w * fx : undefined;
      let height = op.h !== undefined ? op.h * fy : undefined;
      if (width === undefined && height === undefined) fail("resize braucht w und/oder h");
      const ratio = aspectOf(el);
      const keep = op.keepRatio ?? ["image", "svg", "icon", "qr", "device"].includes(el.type);
      if (keep && width !== undefined && height === undefined) height = width / ratio;
      else if (keep && height !== undefined && width === undefined) width = height * ratio;
      const box = worldBox(doc, id)!;
      const next = { ...box, width: width ?? box.width, height: height ?? box.height };
      // Resize around the centre for a calm result.
      next.x = box.x + (box.width - next.width) / 2;
      next.y = box.y + (box.height - next.height) / 2;
      if (el.type === "text") {
        if (op.h !== undefined && op.w === undefined) el.autoHeight = false;
        else if (op.h !== undefined) el.autoHeight = false;
      }
      setWorldBox(doc, id, next);
      if (el.type === "text") refreshHeight(state, el);
      touch(state, id);
      state.pageIndex = pageIndex;
      state.note = "ändert eine Größe";
      return;
    }
    case "fit_text": {
      const el = element(state, op.ref);
      if (el.type !== "text") return fail(`Element „${op.ref}“ ist kein Text`);
      const height = op.maxHeight ?? el.height;
      const size = fitFontSize(
        el,
        { width: el.width, height },
        {
          ...(op.minSize !== undefined ? { min: op.minSize } : {}),
          ...(op.maxSize !== undefined ? { max: op.maxSize } : {}),
          measure: state.measure,
        },
      );
      const scale = size / el.style.fontSize;
      el.style.fontSize = size;
      if (el.marks) {
        for (const mark of el.marks)
          if (mark.fontSize) mark.fontSize = Math.round(mark.fontSize * scale);
      }
      delete el.textScale;
      unbindElement(el, ["text"]);
      refreshHeight(state, el);
      if (!el.autoHeight) el.height = Math.max(el.height, 1);
      touch(state, el.id);
      state.pageIndex = pageOf(state, el.id);
      state.note = "passt die Schriftgröße an";
      return;
    }
    case "replace_image": {
      const id = target(state, op.ref);
      const el = doc.elements[id]!;
      const asset = state.ctx.assets?.[op.assetId];
      if (!asset)
        return fail(`Medium ${op.assetId} gibt es nicht in dieser Organisation (list_media)`);
      if (asset.kind !== "image" && asset.kind !== "svg") {
        return fail(`${asset.name ?? op.assetId} ist kein Bild und keine Grafik (${asset.kind})`);
      }
      if (isImagePlaceholder(el)) {
        const image = imageFromPlaceholder(el, op.assetId);
        if (op.fit) image.fit = op.fit;
        doc.elements[id] = image;
      } else if (el.type === "image") {
        el.assetId = op.assetId;
        el.crop = null;
        if (op.fit) el.fit = op.fit;
        delete el.color;
      } else if (el.type === "svg") {
        el.assetId = op.assetId;
      } else {
        return fail(`Element „${op.ref}“ ist weder Bild noch Bildplatzhalter (${el.type})`);
      }
      touch(state, id);
      state.pageIndex = pageOf(state, id);
      state.note = "tauscht ein Bild";
      return;
    }
    case "add": {
      const pageIndex = pageIndexFor(state, op.page);
      const created = createElement(state, pageIndex, op.element, op.at ?? {}, op.key);
      state.pageIndex = pageIndex;
      state.note = `fügt ${noun(op.element.kind)} ein`;
      return created;
    }
    case "add_block": {
      const pageIndex = pageIndexFor(state, op.page);
      const size = pageSize(doc, doc.pages[pageIndex]);
      let placed: PlacedElements;
      let title: string;
      if (op.blockId.startsWith("saved:")) {
        const data = state.ctx.savedBlocks?.[op.blockId];
        if (!data)
          return fail(`Gespeicherter Baustein „${op.blockId}“ gibt es nicht (search_blocks)`);
        title = "Gespeicherter Baustein";
        placed = placeSavedElements(data, { ...size, kind: doc.kind }, op.atMs ?? 0);
        if (placed.roots.length === 0) return fail("Der gespeicherte Baustein ist leer");
      } else {
        const block = getLibraryBlock(op.blockId);
        if (!block) return fail(`Baustein „${op.blockId}“ gibt es nicht (search_blocks)`);
        if (block.only && block.only !== doc.kind) {
          return fail(
            `„${block.title}“ gibt es nur in ${block.only === "video" ? "Videos" : "Designs"}`,
          );
        }
        title = block.title;
        placed = buildBlock(block, {
          tokens: state.ctx.tokens ?? null,
          style: op.style,
          logos: state.ctx.logos ?? [],
          brandName: state.ctx.brandName,
          measure: undefined as TextMeasure | undefined,
          frame: size,
          kind: doc.kind,
          ...(op.atMs !== undefined ? { at: op.atMs } : {}),
        });
      }
      const root = placed.roots[0]!;
      for (const el of placed.elements) doc.elements[el.id] = el;
      doc.pages[pageIndex]!.children.push(...placed.roots.map((entry) => entry.id));
      if (op.at) {
        const rect = boundingRect(elementBox(root));
        if (op.at.w !== undefined || op.at.h !== undefined) {
          const wanted = resolvePlacement(
            placeContext(state, pageIndex),
            { ...op.at, x: undefined, y: undefined },
            rect,
          );
          const factor =
            op.at.w !== undefined ? wanted.width / rect.width : wanted.height / rect.height;
          if (Number.isFinite(factor) && factor > 0) scaleContent(doc, root.id, factor);
        }
        const now = worldRect(state, root.id);
        const spec: PlacementInput = { ...op.at };
        if (spec.anchor === undefined && spec.zone !== undefined) spec.anchor = "center";
        const hasPosition =
          spec.x !== undefined ||
          spec.y !== undefined ||
          spec.anchor ||
          spec.align ||
          spec.valign ||
          spec.below ||
          spec.above ||
          spec.leftOf ||
          spec.rightOf;
        if (hasPosition) {
          const box = resolvePlacement(
            placeContext(state, pageIndex),
            { ...spec, w: undefined, h: undefined },
            now,
          );
          moveTo(state, root.id, box.x, box.y);
        }
      }
      for (const el of placed.elements) refreshHeight(state, el);
      if (op.key) state.aliases.set(op.key, root.id);
      state.created.push({ id: root.id, ...(op.key ? { alias: op.key } : {}) });
      touch(state, root.id);
      state.pageIndex = pageIndex;
      state.note = `setzt den Baustein „${title}“ ein`;
      return;
    }
    case "remove": {
      const ids = op.refs.map((key) => target(state, key));
      const pageIndex = pageOf(state, ids[0]!);
      removeElements(doc, ids);
      state.removed.push(...ids);
      state.pageIndex = pageIndex;
      state.note = ids.length === 1 ? "entfernt ein Element" : `entfernt ${ids.length} Elemente`;
      return;
    }
    case "duplicate": {
      const ids = op.refs.map((key) => target(state, key));
      const pageIndex = pageOf(state, ids[0]!);
      const copies = duplicateElements(doc, ids, op.offset ?? { x: 24, y: 24 });
      if (op.key && copies[0]) state.aliases.set(op.key, copies[0]);
      for (const id of copies) {
        state.created.push({ id, ...(op.key && id === copies[0] ? { alias: op.key } : {}) });
        touch(state, id);
      }
      state.pageIndex = pageIndex;
      state.note = "dupliziert Elemente";
      return;
    }
    case "reorder": {
      const id = target(state, op.ref);
      if (typeof op.to === "string") {
        reorderElements(doc, [id], op.to as ReorderMode);
      } else {
        const otherKey = "above" in op.to ? op.to.above : op.to.below;
        const other = target(state, otherKey);
        const parents = buildParentMap(doc);
        const own = parents.get(id);
        const theirs = parents.get(other);
        const list = (parent: typeof own) =>
          parent?.kind === "page"
            ? doc.pages[parent.pageIndex]?.children
            : parent?.kind === "group"
              ? (doc.elements[parent.groupId] as { children: string[] }).children
              : undefined;
        const mine = list(own);
        if (!mine || (mine !== list(theirs) && JSON.stringify(own) !== JSON.stringify(theirs))) {
          return fail(
            "Die Elemente liegen nicht in derselben Ebene (Seite bzw. Gruppe) – reorder geht nur dort",
          );
        }
        mine.splice(mine.indexOf(id), 1);
        const at = mine.indexOf(other);
        mine.splice("above" in op.to ? at + 1 : at, 0, id);
      }
      touch(state, id);
      state.pageIndex = pageOf(state, id);
      state.note = "ändert die Reihenfolge";
      return;
    }
    case "group": {
      const ids = op.refs.map((key) => target(state, key));
      const groupId = groupElements(doc, ids);
      if (!groupId)
        return fail("Gruppieren geht nur für mindestens zwei Elemente in derselben Ebene");
      const group = doc.elements[groupId]!;
      if (op.name) group.name = op.name;
      if (op.key) state.aliases.set(op.key, groupId);
      state.created.push({ id: groupId, ...(op.key ? { alias: op.key } : {}) });
      touch(state, groupId);
      state.pageIndex = pageOf(state, groupId);
      state.note = "gruppiert Elemente";
      return;
    }
    case "ungroup": {
      const id = target(state, op.ref);
      const el = doc.elements[id]!;
      if (el.type !== "group") return fail(`Element „${op.ref}“ ist keine Gruppe`);
      const pageIndex = pageOf(state, id);
      const children = ungroupElement(doc, id);
      touch(state, ...children);
      state.removed.push(id);
      state.pageIndex = pageIndex;
      state.note = "löst eine Gruppe auf";
      return;
    }
    case "align": {
      const ids = op.refs.map((key) => target(state, key));
      const pageIndex = pageOf(state, ids[0]!);
      const rects = ids.map((id) => worldRect(state, id));
      const size = pageSize(doc, doc.pages[pageIndex]);
      const bounds =
        op.to === "page" ? { x: 0, y: 0, width: size.width, height: size.height } : unionOf(rects);
      ids.forEach((id, index) => {
        const rect = rects[index]!;
        let { x, y } = rect;
        if (op.edge === "left") x = bounds.x;
        else if (op.edge === "right") x = bounds.x + bounds.width - rect.width;
        else if (op.edge === "center") x = bounds.x + (bounds.width - rect.width) / 2;
        else if (op.edge === "top") y = bounds.y;
        else if (op.edge === "bottom") y = bounds.y + bounds.height - rect.height;
        else y = bounds.y + (bounds.height - rect.height) / 2;
        shiftWorld(state, id, x - rect.x, y - rect.y);
      });
      touch(state, ...ids);
      state.pageIndex = pageIndex;
      state.note = "richtet Elemente aus";
      return;
    }
    case "distribute": {
      const ids = op.refs.map((key) => target(state, key));
      const pageIndex = pageOf(state, ids[0]!);
      const horizontal = op.axis === "horizontal";
      const entries = ids
        .map((id) => ({ id, rect: worldRect(state, id) }))
        .sort((a, b) => (horizontal ? a.rect.x - b.rect.x : a.rect.y - b.rect.y));
      const first = entries[0]!.rect;
      const last = entries[entries.length - 1]!.rect;
      const total = entries.reduce(
        (sum, entry) => sum + (horizontal ? entry.rect.width : entry.rect.height),
        0,
      );
      const span = horizontal ? last.x + last.width - first.x : last.y + last.height - first.y;
      const gap = op.gap ?? (span - total) / (entries.length - 1);
      let cursor = horizontal ? first.x : first.y;
      for (const { id, rect } of entries) {
        if (horizontal) shiftWorld(state, id, cursor - rect.x, 0);
        else shiftWorld(state, id, 0, cursor - rect.y);
        cursor += (horizontal ? rect.width : rect.height) + gap;
      }
      touch(state, ...ids);
      state.pageIndex = pageIndex;
      state.note = "verteilt Elemente gleichmäßig";
      return;
    }
    case "tidy": {
      const ids = op.refs.map((key) => target(state, key));
      const pageIndex = pageOf(state, ids[0]!);
      const items = ids.map((id) => ({ id, rect: worldRect(state, id) }));
      const layout = tidyLayout(items, op.columns !== undefined ? { columns: op.columns } : {});
      for (const item of items) {
        const next = layout.get(item.id);
        if (next) shiftWorld(state, item.id, next.x - item.rect.x, next.y - item.rect.y);
      }
      touch(state, ...ids);
      state.pageIndex = pageIndex;
      state.note = "räumt Elemente auf";
      return;
    }
    case "apply_style": {
      const pages =
        op.page !== undefined ? [pageIndexFor(state, op.page)] : doc.pages.map((_, index) => index);
      if (!op.style && !op.look) return fail("apply_style braucht style oder look");
      let changed = 0;
      const notes: string[] = [];
      if (op.style) {
        const style = getBrandStyle(op.style);
        if (!style) {
          return fail(
            `Stil „${op.style}“ gibt es nicht. Verfügbar: ${BRAND_STYLES.map((entry) => `${entry.id} (${entry.label})`).join(", ")}`,
          );
        }
        const result = restyleDocument(doc, style, pages);
        changed += result.changedElements + result.changedPages;
        if (changed === 0) {
          return fail(
            result.from === style.id
              ? `Die Datei hat bereits den Stil „${style.label}“`
              : "Es gab nichts anzupassen: keine Farben oder Schriften, die einem Stil zuzuordnen sind",
          );
        }
        notes.push(`Stil „${style.label}“`);
      }
      if (op.look) {
        const lookId = op.look;
        const look = state.ctx.savedLooks?.[lookId];
        if (!look) return fail(`Look „${lookId}“ gibt es nicht (search_blocks, Looks)`);
        let images = 0;
        for (const index of pages) {
          const visit = (ids: string[]) => {
            for (const id of ids) {
              const element = doc.elements[id];
              if (element?.type === "image") {
                element.color = {
                  ...look.values,
                  look: lookId.replace(/^saved:/, "").slice(0, 64),
                };
                images++;
              } else if (element?.type === "group") visit(element.children);
            }
          };
          visit(doc.pages[index]?.children ?? []);
        }
        if (images === 0) return fail("Auf diesen Seiten gibt es keine Bilder für den Look");
        changed += images;
        notes.push("Look");
      }
      state.pageIndex = pages[0] ?? 0;
      state.note = `wendet ${notes.join(" und ")} an`;
      return;
    }
    case "page_add": {
      const index = op.at ? Math.min(op.at - 1, doc.pages.length) : doc.pages.length;
      const bg = op.background ? colorValue(state, op.background).hex : "#ffffff";
      addPage(doc, index, bg);
      state.pageIndex = index;
      state.note = "legt eine Seite an";
      return;
    }
    case "page_duplicate": {
      const index = duplicatePage(doc, pageIndexFor(state, op.page));
      state.pageIndex = index;
      state.note = "dupliziert eine Seite";
      return;
    }
    case "page_remove": {
      const index = pageIndexFor(state, op.page);
      if (doc.pages.length <= 1) return fail("Die letzte Seite lässt sich nicht entfernen");
      const ids = Object.keys(doc.elements);
      removePage(doc, index);
      for (const id of ids) if (!doc.elements[id]) state.removed.push(id);
      state.pageIndex = Math.min(index, doc.pages.length - 1);
      state.note = "entfernt eine Seite";
      return;
    }
    case "page_move": {
      const from = pageIndexFor(state, op.page);
      const to = pageIndexFor(state, op.to);
      movePage(doc, from, to);
      state.pageIndex = to;
      state.note = "verschiebt eine Seite";
      return;
    }
    case "page_resize": {
      const index = pageIndexFor(state, op.page);
      if (doc.format.print)
        return fail("Druckformate haben feste Seitengrößen (create_document mit anderem Format)");
      resizePage(doc, index, { width: op.width, height: op.height }, op.scale);
      state.pageIndex = index;
      state.note = "ändert die Seitengröße";
      return;
    }
    case "set_time": {
      const el = element(state, op.ref);
      if (doc.kind !== "video") return fail("Zeiten gibt es nur in Videos");
      if (op.clear) delete el.time;
      else {
        const start = op.start ?? el.time?.start ?? 0;
        const end = op.end ?? el.time?.end;
        if (end === undefined) return fail("set_time braucht end (oder clear)");
        if (end <= start) return fail("Das Ende muss nach dem Anfang liegen");
        el.time = { start, end };
      }
      touch(state, el.id);
      state.pageIndex = pageOf(state, el.id);
      state.note = "ändert die Sichtbarkeit in der Zeit";
      return;
    }
    case "set_animation": {
      const el = element(state, op.ref);
      if (doc.kind !== "video") return fail("Animationen gibt es nur in Videos");
      const animation = { ...(el.animation ?? {}) };
      for (const slot of ["in", "out"] as const) {
        const value = op[slot];
        if (value === null) delete animation[slot];
        else if (value) animation[slot] = { preset: value.preset, duration: value.duration ?? 500 };
      }
      if (op.loop === null) delete animation.loop;
      else if (op.loop) animation.loop = op.loop;
      if (Object.keys(animation).length === 0) delete el.animation;
      else el.animation = elementAnimationSchema.parse(animation);
      touch(state, el.id);
      state.pageIndex = pageOf(state, el.id);
      state.note = "ändert die Animation";
      return;
    }
  }
}

function noun(kind: AddElement["kind"]): string {
  return (
    {
      text: "einen Text",
      shape: "eine Form",
      image: "ein Bild",
      icon: "ein Icon",
      qr: "einen QR-Code",
      table: "eine Tabelle",
      chart: "ein Diagramm",
      device: "ein Geräte-Mockup",
    } as const
  )[kind];
}

function unionOf(rects: Rect[]): Rect {
  const left = Math.min(...rects.map((rect) => rect.x));
  const top = Math.min(...rects.map((rect) => rect.y));
  const right = Math.max(...rects.map((rect) => rect.x + rect.width));
  const bottom = Math.max(...rects.map((rect) => rect.y + rect.height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

/** Moves an element by an offset in page space. */
function shiftWorld(state: State, id: string, dx: number, dy: number) {
  if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) return;
  const box = worldBox(state.doc, id)!;
  setWorldBox(state.doc, id, { ...box, x: box.x + dx, y: box.y + dy });
}

function applyStyle(state: State, el: SceneElement, op: Extract<EditOp, { op: "set_style" }>) {
  if (op.fontFamily !== undefined) checkFont(state, op.fontFamily);
  const textual = el.type === "text";
  if (op.textStyle !== undefined) {
    if (!textual) fail("textStyle gilt nur für Texte");
    const tokens = state.ctx.tokens;
    if (!tokens)
      fail(
        "Diese Organisation hat kein Brand Kit – Schrift direkt über fontFamily, fontSize … setzen",
      );
    if (!brandTextStyle(tokens!, op.textStyle)) {
      fail(
        `Textstil „${op.textStyle}“ gibt es nicht (${tokens!.textStyles.map((style) => style.key).join(", ")})`,
      );
    }
    bindTextStyle(el, op.textStyle, tokens!, brandScale(state.doc), {
      withColor: op.color === undefined,
    });
  }
  const typographic =
    op.fontFamily !== undefined ||
    op.fontSize !== undefined ||
    op.fontWeight !== undefined ||
    op.italic !== undefined ||
    op.underline !== undefined ||
    op.strike !== undefined ||
    op.uppercase !== undefined ||
    op.align !== undefined ||
    op.verticalAlign !== undefined ||
    op.lineHeight !== undefined ||
    op.letterSpacing !== undefined;
  if (typographic) {
    if (el.type !== "text" && el.type !== "captions") {
      fail(`Schriftangaben gelten nur für Texte, nicht für ${el.type}`);
    }
    const style = (el as TextElement).style;
    if (op.fontFamily !== undefined) style.fontFamily = op.fontFamily;
    if (op.fontSize !== undefined) {
      style.fontSize = op.fontSize;
      if (el.type === "text") delete el.textScale;
    }
    if (op.fontWeight !== undefined) style.fontWeight = op.fontWeight;
    if (op.italic !== undefined) style.italic = op.italic;
    if (op.underline !== undefined) style.underline = op.underline;
    if (op.strike !== undefined) style.strike = op.strike;
    if (op.uppercase !== undefined) style.uppercase = op.uppercase;
    if (op.align !== undefined) style.align = op.align;
    if (op.verticalAlign !== undefined) style.verticalAlign = op.verticalAlign;
    if (op.lineHeight !== undefined) style.lineHeight = op.lineHeight;
    if (op.letterSpacing !== undefined) style.letterSpacing = op.letterSpacing;
    if (op.textStyle === undefined) {
      unbindElement(el, ["text"]);
    }
    // Explicit values replace what formatted parts of the text had.
    if (
      el.type === "text" &&
      el.marks &&
      (op.fontSize !== undefined || op.fontFamily !== undefined || op.fontWeight !== undefined)
    ) {
      for (const mark of el.marks) {
        if (op.fontSize !== undefined) delete mark.fontSize;
        if (op.fontFamily !== undefined) delete mark.fontFamily;
        if (op.fontWeight !== undefined) delete mark.fontWeight;
      }
    }
  }
  if (op.color !== undefined) setColor(state, el, "color", op.color);
  if (op.opacity !== undefined) el.opacity = op.opacity;
  if (op.rotation !== undefined) el.rotation = op.rotation;
  if (op.radius !== undefined) {
    if (el.type !== "shape" && el.type !== "image")
      fail(`Ecken-Radius gibt es nicht bei ${el.type}`);
    (el as { radius: number }).radius = op.radius;
    unbindElement(el, ["radius"]);
  }
  if (op.shadow !== undefined) {
    if (op.shadow === null) delete el.shadow;
    else el.shadow = shadowSchema.parse({ ...(el.shadow ?? {}), ...op.shadow });
  }
  if (op.effect !== undefined) {
    if (el.type !== "text") fail("Texteffekte gibt es nur bei Texten");
    if (op.effect === null) delete (el as TextElement).effect;
    else (el as TextElement).effect = op.effect;
  }
  if (op.name !== undefined) el.name = op.name;
  if (op.hidden !== undefined) el.hidden = op.hidden;
  if (op.locked !== undefined) el.locked = op.locked;
}

// ---------------------------------------------------------------------------
// Creating elements
// ---------------------------------------------------------------------------

function defaultTextColor(state: State, pageIndex: number): string {
  const background = state.doc.pages[pageIndex]!.background;
  return contrastColor(paintPreviewColor(background));
}

function createElement(
  state: State,
  pageIndex: number,
  spec: AddElement,
  at: PlacementInput,
  alias?: string,
) {
  const { doc } = state;
  const size = pageSize(doc, doc.pages[pageIndex]);
  const short = Math.min(size.width, size.height);
  const margin = Math.round(short * 0.06);
  let created: SceneElement;
  switch (spec.kind) {
    case "text": {
      const role = spec.role ?? "body";
      const look = ROLE_LOOK[role];
      const tokens = state.ctx.tokens;
      const styleKey = spec.textStyle ?? (spec.role ? ROLE_STYLE_KEY[role] : undefined);
      if (spec.fontFamily) checkFont(state, spec.fontFamily);
      const width = at.w !== undefined ? undefined : Math.round(size.width - margin * 2);
      const element = createTextElement({
        text: spec.text,
        width: width ?? 400,
        style: {
          fontFamily: spec.fontFamily ?? "Inter",
          fontSize: spec.fontSize ?? Math.round(short * look.size),
          fontWeight: spec.fontWeight ?? look.weight,
          lineHeight: look.lineHeight,
          color: defaultTextColor(state, pageIndex),
          ...(spec.align ? { align: spec.align } : {}),
        },
      });
      if (styleKey && tokens && brandTextStyle(tokens, styleKey)) {
        const keepSize = spec.fontSize;
        bindTextStyle(element, styleKey, tokens, brandScale(doc), {
          withColor: spec.color === undefined,
        });
        if (keepSize !== undefined) {
          element.style.fontSize = keepSize;
          unbindElement(element, ["text"]);
        }
        if (spec.fontFamily) element.style.fontFamily = spec.fontFamily;
        if (spec.fontWeight) element.style.fontWeight = spec.fontWeight;
        if (spec.align) element.style.align = spec.align;
      } else if (styleKey && !tokens && spec.textStyle) {
        fail(
          "Diese Organisation hat kein Brand Kit – textStyle weglassen und role bzw. fontSize verwenden",
        );
      }
      if (spec.color) setColor(state, element, "color", spec.color);
      if (spec.role) element.name = ROLE_NAME[spec.role];
      element.height = Math.max(1, textContentHeight(element, state.measure));
      created = element;
      break;
    }
    case "shape": {
      const element = createShapeElement(spec.shape, {
        width: Math.round(short * (spec.shape === "line" ? 0.5 : 0.3)),
        height: spec.shape === "line" ? 8 : Math.round(short * 0.3),
        ...(spec.radius !== undefined ? { radius: spec.radius } : {}),
      });
      if (spec.gradient) element.fill = spec.gradient;
      else if (spec.fill) setColor(state, element, "fill", spec.fill);
      if (spec.stroke) {
        element.stroke = {
          color: colorValue(state, spec.stroke.color).hex,
          width: spec.stroke.width,
          dash: "solid",
        };
        const { key } = colorValue(state, spec.stroke.color);
        if (key && state.ctx.tokens) bindColor(element, "stroke", key, state.ctx.tokens);
      } else if (spec.shape === "line" && spec.fill) {
        // A line takes its colour from the stroke.
        element.stroke = { color: colorValue(state, spec.fill).hex, width: 4, dash: "solid" };
        const { key } = colorValue(state, spec.fill);
        if (key && state.ctx.tokens) bindColor(element, "stroke", key, state.ctx.tokens);
      }
      created = element;
      break;
    }
    case "image": {
      const asset = state.ctx.assets?.[spec.assetId];
      if (!asset)
        return fail(`Medium ${spec.assetId} gibt es nicht in dieser Organisation (list_media)`);
      if (asset.kind !== "image" && asset.kind !== "svg") {
        return fail(`${asset.name ?? spec.assetId} ist kein Bild und keine Grafik (${asset.kind})`);
      }
      const natural =
        asset.width && asset.height
          ? { width: asset.width, height: asset.height }
          : { width: 4, height: 3 };
      const fitted = fitSize(
        { width: natural.width * 1000, height: natural.height * 1000 },
        { width: size.width * 0.8, height: size.height * 0.6 },
      );
      let { width, height } = fitted;
      if (at.w !== undefined || at.h !== undefined) {
        const wanted = resolvePlacement(
          placeContext(state, pageIndex),
          {
            ...(at.w !== undefined ? { w: at.w } : {}),
            ...(at.h !== undefined ? { h: at.h } : {}),
            ...(at.unit ? { unit: at.unit } : {}),
          },
          { width, height },
        );
        if (at.w !== undefined && at.h === undefined) {
          width = wanted.width;
          height = wanted.width * (natural.height / natural.width);
        } else if (at.h !== undefined && at.w === undefined) {
          height = wanted.height;
          width = wanted.height * (natural.width / natural.height);
        } else {
          width = wanted.width;
          height = wanted.height;
        }
      }
      created =
        asset.kind === "svg"
          ? createSvgElement(spec.assetId, { width, height })
          : createImageElement(spec.assetId, {
              width,
              height,
              ...(spec.fit ? { fit: spec.fit } : {}),
              ...(spec.radius !== undefined ? { radius: spec.radius } : {}),
            });
      break;
    }
    case "icon": {
      const paths = state.ctx.icons?.[`${spec.name}:${spec.weight}`];
      if (!paths) return fail(`Icon „${spec.name}“ gibt es nicht (search_icons)`);
      const element = createIconElement(spec.name, spec.weight as IconWeight, paths, {
        width: Math.round(short * 0.12),
        height: Math.round(short * 0.12),
        color: defaultTextColor(state, pageIndex),
      });
      if (spec.color) setColor(state, element, "color", spec.color);
      created = element;
      break;
    }
    case "qr": {
      const ecc = spec.logoAssetId ? QR_LOGO_ECC : "M";
      if (!qrMatrix(spec.value, ecc)) return fail("Der Inhalt ist zu lang für einen QR-Code");
      if (spec.logoAssetId) {
        const asset = state.ctx.assets?.[spec.logoAssetId];
        if (!asset || (asset.kind !== "image" && asset.kind !== "svg")) {
          return fail(`Logo ${spec.logoAssetId} ist kein Bild oder keine Grafik der Organisation`);
        }
      }
      const edge = Math.round(short * 0.22);
      const element = createQrElement(spec.value, {
        width: edge,
        height: edge,
        ecc,
        ...(spec.logoAssetId
          ? { logo: { assetId: spec.logoAssetId, size: 0.22, clear: true } }
          : {}),
      });
      if (spec.color) setColor(state, element, "color", spec.color);
      created = element;
      break;
    }
    case "table": {
      const width = Math.round(size.width - margin * 2);
      const element = createTableElement({
        cells: spec.rows.map((row) => [...row]),
        width,
        fontSize: spec.fontSize ?? Math.max(14, Math.round(short * 0.026)),
        header: spec.header,
        ...(spec.zebra !== undefined ? { zebra: spec.zebra } : {}),
      });
      created = element;
      break;
    }
    case "chart": {
      let data: Pick<ChartElement, "labels" | "series"> | null = null;
      if (spec.table) {
        data = parseChartTable(spec.table);
        if (!data) return fail("In der Tabelle stehen keine Zahlen");
      } else if (spec.series) {
        const length = Math.max(...spec.series.map((series) => series.values.length));
        data = {
          labels: spec.labels ?? Array.from({ length }, (_, index) => String(index + 1)),
          series: spec.series,
        } as Pick<ChartElement, "labels" | "series">;
      }
      const compact = spec.chart === "pie" || spec.chart === "donut" || spec.chart === "progress";
      const width = Math.round(Math.min(size.width, size.height * 1.4) * (compact ? 0.5 : 0.7));
      const height = Math.round(
        compact ? (spec.chart === "progress" ? width : width * 1.05) : width * 0.62,
      );
      const fontSize = Math.max(8, Math.round(Math.min(width, height) / 20));
      const element = createChartElement(spec.chart, {
        width,
        height,
        fontSize,
        lineWidth: Math.max(1.5, Math.round(fontSize / 5)),
        barRadius: Math.round(fontSize / 3),
        stacked: spec.stacked,
        ...(spec.title || data ? { title: spec.title } : {}),
        ...(data ? { labels: data.labels, series: data.series, unit: spec.unit } : {}),
        ...(spec.showValues !== undefined ? { showValues: spec.showValues } : {}),
      });
      const tokens = state.ctx.tokens;
      if (tokens) {
        const palette = brandChartPalette(tokens);
        if (palette) {
          element.palette = palette;
          element.brandPalette = true;
        }
        if (!bindColor(element, "color", "text", tokens))
          bindColor(element, "color", "primary", tokens);
      }
      created = element;
      break;
    }
    case "device": {
      const screenshot = spec.assetId ? state.ctx.assets?.[spec.assetId] : undefined;
      if (spec.assetId && screenshot?.kind !== "image") {
        return fail(`Screenshot ${spec.assetId} ist kein Bild der Organisation`);
      }
      const facts = screenshot
        ? { width: screenshot.width ?? null, height: screenshot.height ?? null }
        : null;
      const device = spec.device ?? deviceForScreenshot(facts).device;
      const model = deviceModel(device);
      const landscape =
        deviceCanRotate(model) && facts?.width && facts.height
          ? facts.width > facts.height !== model.screen.width > model.screen.height
          : false;
      const perspectives = devicePerspectives(device);
      const tilt = {
        ...(perspectives.find((entry) => entry.id === spec.view) ?? perspectives[0]!).tilt,
      };
      const props = { device, landscape, tilt };
      const box = deviceBoxSize(props, deviceContentAspect(facts), {
        width: size.width * (at.w !== undefined ? 1 : 0.7),
        height: size.height * 0.7,
      });
      const side = Math.max(box.width, box.height);
      created = {
        ...createDeviceElement(props),
        ...box,
        shadow: {
          x: 0,
          y: Math.round(side * 0.025),
          blur: Math.round(side * 0.07),
          color: "#00000038",
        },
        ...(spec.assetId ? { assetId: spec.assetId } : {}),
        ...(spec.url ? { url: spec.url } : {}),
      } as SceneElement;
      break;
    }
  }
  if (spec.kind !== "image" && (at.w !== undefined || at.h !== undefined)) {
    const wanted = resolvePlacement(
      placeContext(state, pageIndex),
      {
        ...(at.w !== undefined ? { w: at.w } : {}),
        ...(at.h !== undefined ? { h: at.h } : {}),
        ...(at.unit ? { unit: at.unit } : {}),
      },
      created,
    );
    const ratio = created.width / Math.max(1, created.height);
    const keep = spec.kind === "icon" || spec.kind === "qr" || spec.kind === "device";
    created.width = keep && at.w === undefined ? wanted.height * ratio : wanted.width;
    created.height = keep && at.h === undefined ? wanted.width / ratio : wanted.height;
    if (created.type === "text") {
      if (at.h !== undefined) created.autoHeight = false;
      else created.height = Math.max(1, textContentHeight(created, state.measure));
    }
  }
  const placed = resolvePlacement(
    placeContext(state, pageIndex),
    { ...at, w: undefined, h: undefined },
    { width: created.width, height: created.height },
  );
  created.x = placed.x;
  created.y = placed.y;
  insertElements(doc, pageIndex, [created]);
  if (alias) state.aliases.set(alias, created.id);
  state.created.push({ id: created.id, ...(alias ? { alias } : {}) });
  touch(state, created.id);
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

/**
 * Applies the operations one after the other to a copy of `base`. Throws an
 * `EditError` (naming the operation) as soon as one cannot be applied – the
 * caller then has changed nothing. `base` itself is never mutated.
 */
export function applyEdits(
  base: SceneDocument,
  ops: readonly EditOp[],
  ctx: EditContext,
): EditOutcome {
  const measure = ctx.measure ?? ESTIMATED_MEASURE;
  const aliases = new Map<string, string>();
  const steps: EditStep[] = [];
  const changed = new Set<string>();
  const created: EditOutcome["created"] = [];
  const removed: string[] = [];
  let current = base;
  syncRefs(ctx.refs, current);
  for (const [index, op] of ops.entries()) {
    const next = clone(current);
    const state: State = {
      doc: next,
      ctx,
      aliases,
      measure,
      touched: new Set(),
      rewrites: new Set(),
      pageIndex: null,
      created: [],
      removed: [],
      note: "",
    };
    try {
      applyOp(state, op);
    } catch (error) {
      if (error instanceof OpError || error instanceof PlacementError) {
        throw new EditError(`Operation ${index + 1} (${op.op}): ${error.message}`, index);
      }
      if (error instanceof z.ZodError) {
        throw new EditError(`Operation ${index + 1} (${op.op}): ${z.prettifyError(error)}`, index);
      }
      throw error;
    }
    syncRefs(ctx.refs, next);
    const liveOps = diff(current, next);
    for (const id of state.touched) changed.add(id);
    for (const entry of state.created) created.push({ ...entry, ref: refOf(ctx.refs, entry.id) });
    removed.push(...state.removed);
    steps.push({
      index,
      op,
      ops: liveOps,
      touched: [...state.touched].filter((id) => next.elements[id]),
      pageIndex: state.pageIndex,
      rewrites: [...state.rewrites],
      note: state.note,
    });
    current = next;
  }
  return {
    doc: current,
    steps,
    changed: [...changed].filter((id) => current.elements[id]),
    created: created.filter((entry) => current.elements[entry.id]),
    removed,
  };
}
